import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {Table, Card, Input, Typography, Tag, Space, Button, Modal, Form, Select, message, AutoComplete} from 'antd';
import {UserOutlined, SearchOutlined, PlusOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {Customer as CustomerType} from "../../types";
import api from '../../api';

const {Title} = Typography;
const {Option} = Select;

const nationalityOptions = ["Japan", "Taiwan", "Hong Kong", "China", "Singapore", "Korea"];

const CustomersPage: React.FC = () => {
    const [customers, setCustomers] = useState<CustomerType[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [agents, setAgents] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [mmidOptions, setMmidOptions] = useState<{ value: string }[]>([]);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const navigate = useNavigate();
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCustomers(pagination.current, pagination.pageSize, searchText);
    }, [pagination.current, pagination.pageSize, searchText]);

    useEffect(() => {
        fetchAgents()
    }, []);

    const fetchAgents = async () => {
        try {
            const response = await api.get('/daia/agents/');
            const agentsArray = response.data.results || response.data || [];
            setAgents(agentsArray);
        } catch (error) {
            message.error("Failed to fetch agents");
            setAgents([]);
        }
    };

    const fetchCustomers = async (page = 1, pageSize = 10, search = "") => {
        setLoading(true);
        try {
            const response = await api.get('/daia/customers/', {
                params: {
                    page,
                    page_size: pageSize,
                    search,
                },
            });

            const data = response.data.results || response.data;
            setCustomers(data);
            setTotalCustomers(response.data.count || 0);
        } catch (error) {
            console.error('Error fetching customers:', error);
            message.error("Failed to load customers");
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (value: string) => {
        if (value.length >= 2) {
            try {
                const res = await api.get(`/cc/customers-mmids/?q=${value}`);
                setMmidOptions(res.data);  // Assuming res.data is a list
            } catch (err) {
                console.error("Failed to fetch MMID suggestions", err);
                console.log(err)
            }
        } else {
            setMmidOptions([]);
        }
    };

    const handleCreateCustomer = async () => {
        try {
            const values = await form.validateFields();

            const payload = {
                ...values,
                agent: values.agent === 'casino_club' ? null : values.agent,
            };

            await api.post("/daia/customers/", payload);

            message.success("Customer created successfully");
            setIsModalVisible(false);
            form.resetFields();

            const response = await api.get('/daia/customers/');
            const customerList = Array.isArray(response.data) ? response.data : response.data.results || [];
            setCustomers(customerList);
            setSubmitError(null);
        } catch (error: any) {
            if (error?.response?.data) {
                const data = error.response.data;

                // 🔹 Check for known field errors like `mmid`
                if (typeof data === "object") {
                    const messages = Object.entries(data).map(([field, val]) => {
                        const msg = Array.isArray(val) ? val.join(', ') : val;
                        return `${field}: ${msg}`;
                    });

                    setSubmitError(messages.join('\n'));
                } else if (typeof data === "string" && data.includes("duplicate key")) {
                    // 🔸 Fallback in case of raw error string
                    setSubmitError("A customer with this MMID already exists.");
                } else {
                    setSubmitError("An unexpected error occurred.");
                }
            } else {
                setSubmitError("A network or server error occurred.");
            }
        }
    };

    const filteredCustomers = customers.filter(customer =>
        `${customer.first_name} ${customer.last_name}`.toLowerCase().includes(searchText.toLowerCase()) ||
        (customer.mmid && customer.mmid.toLowerCase().includes(searchText.toLowerCase()))
    );

    const columns: ColumnsType<CustomerType> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            align: 'center',
            render: (text: string) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: 'Full Name',
            key: 'full_name',
            render: (_, record) => (
                <Space>
                    <UserOutlined style={{color: '#1890ff'}}/>
                    <strong>{record.first_name} {record.last_name}</strong>
                </Space>
            ),
        },
        {
            title: 'Nationality',
            dataIndex: 'nationality',
            key: 'nationality',
            align: 'center',
        },
        {
            title: 'MMID',
            dataIndex: 'mmid',
            key: 'mmid',
            render: (mmid: string | null) => <Tag color="gold">{mmid || "N/A"}</Tag>,
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone: string | null) => phone ?? 'N/A',
        },
        {
            title: 'Agent',
            dataIndex: 'agent_name',
            key: 'agent',
            render: (agent_name: string | null) => agent_name || 'Casino Club',
        }
    ];

    return (
        <Card
            title={
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                    }}
                >
                    <Title level={2} style={{margin: 0}}>DAIA Customers</Title>
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={() => {
                            form.resetFields();
                            setIsModalVisible(true);
                        }}
                    >
                        Create Customer
                    </Button>
                </div>
            }
            style={{margin: 16, padding: 16}}
        >
            <Input
                placeholder="Search customers by name or MMID..."
                prefix={<SearchOutlined/>}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{marginBottom: 16, maxWidth: 320}}
            />

            <Table
                scroll={{x: "max-content"}}
                loading={loading}
                dataSource={customers}
                columns={columns}
                rowKey="id"
                bordered
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: totalCustomers,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                onRow={(record) => ({
                    onClick: () => navigate(`/daia/customers/${record.id}`),
                    style: {cursor: "pointer", transition: "background 0.2s ease-in-out"},
                })}
            />

            <Modal
                title="Create New Customer"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                okText="Create"
            >
                {submitError && (
                    <div style={{marginBottom: 16, color: "red", whiteSpace: "pre-line"}}>
                        {submitError}
                    </div>
                )}
                <Form form={form} layout="vertical" onFinish={handleCreateCustomer}>
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{required: true, message: "First name is required"}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{required: true, message: "Last name is required"}]}
                    >
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="nationality"
                        label="Nationality"
                        rules={[{required: true}]}
                    >
                        <AutoComplete
                            options={nationalityOptions.map((n) => ({value: n}))}
                            filterOption={(inputValue, option) =>
                                option!.value.toLowerCase().includes(inputValue.toLowerCase())
                            }
                        >
                            <Input/>
                        </AutoComplete>
                    </Form.Item>
                    <Form.Item
                        name="mmid"
                        label="MMID"
                        rules={[
                            {required: true, message: "MMID is required"},
                            {pattern: /^\d{8}$/, message: "MMID must be exactly 8 digits"},
                            {
                                validator: async (_, value) => {
                                    if (!value || !/^\d{8}$/.test(value)) return Promise.resolve();
                                    try {
                                        const res = await api.get(`/cc/customers-mmids/?q=${value}`);
                                        const exists = res.data.includes(value);
                                        return exists
                                            ? Promise.resolve()
                                            : Promise.reject("MMID not found");
                                    } catch {
                                        return Promise.reject("Error validating MMID");
                                    }
                                },
                            },
                        ]}
                    >
                        <AutoComplete
                            options={mmidOptions.map((mmid) => ({value: mmid}))}
                            onSearch={handleSearch}
                            placeholder="Type MMID (8 digits)"
                            allowClear
                        />
                    </Form.Item>
                    <Form.Item name="phone" label="Phone">
                        <Input/>
                    </Form.Item>
                    <Form.Item
                        name="agent"
                        label="Agent"
                        rules={[{required: true, message: "Please select an agent"}]}
                    >
                        <Select placeholder="Select agent">
                            {agents.map((agent: any) => (
                                <Option key={agent.id} value={agent.id}>
                                    {agent.first_name} {agent.last_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default CustomersPage;
